/*************************
 * @license
 * Copyright 2024 Myenergi Ltd. All rights reserved.
 * No part of this work may be reproduced, stored in a retrieval system of any nature, or transmitted, in any form or by any means without the prior written permission of Myenergi Ltd., the copyright owner.
 * If any unauthorised acts are carried out in relation to this copyright work, a civil claim for damages may be made and/or a criminal prosecution may result.
 *************************/
import customerDataApi from "containers/Customers/api";
import {
  CustomerData,
  CustomerSearch,
  CustomerSearchParams,
  CustomerSearchResult,
} from "containers/Customers/types/Customers";
import { t } from "i18next";
import { action, makeObservable, observable } from "mobx";
import { persist } from "mobx-persist";
import { AuthStore } from "./AuthStore";
import { NotificationStore, AlertType } from "./NotificationStore";

export class CustomersStore {
  private authStore;
  private notificationStore;

  public constructor(auth: AuthStore, notification: NotificationStore) {
    this.authStore = auth;
    this.notificationStore = notification;
    this.checkedCustomers = [];
    makeObservable(this);
  }

  @observable @persist("object") public customerSearchResult:
    | CustomerSearchResult
    | undefined;
  @observable @persist("object") public selectedCustomer:
    | CustomerData
    | undefined;
  @observable @persist("object") public checkedCustomers: CustomerSearch[];
  @observable @persist("object") public customerSearchParams:
    | CustomerSearchParams
    | undefined;

  @observable @persist public isLoading = false;

  @action.bound
  public setIsLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
  }

  public search = async (value = "") => {
    if (this.isLoading) return;

    try {
      this.setIsLoading(true);

      const searchResults = await customerDataApi.search(
        { filter: value },
        this.authStore.jwtToken,
      );

      if (searchResults.customers.length === 0) {
        this.notificationStore.triggerNotification(
          t("search.couldntFindCustomer", {
            searchTextValue: value,
          }),
          AlertType.warning,
        );
      }

      this.setCustomerSearchParams({ filter: value });
      this.setCustomerSearchResult(searchResults);
    } catch {
      this.notificationStore.triggerNotification(
        t("search.serverError"),
        AlertType.error,
      );
    } finally {
      this.setIsLoading(false);
    }
  };

  public getCustomer = async (customerId: string) => {
    if (this.isLoading) return;

    try {
      this.setIsLoading(true);

      const fetchedCustomer = await customerDataApi.getCustomerData(
        customerId,
        this.authStore.jwtToken,
      );

      this.setSelectedCustomer(fetchedCustomer);
    } catch {
      this.notificationStore.triggerNotification(
        t("customerPage.errorMessages.fetchCustomerData"),
        AlertType.error,
      );
    } finally {
      this.setIsLoading(false);
    }
  };

  @action.bound
  public setCustomerSearchResult(searchResult: CustomerSearchResult): void {
    this.customerSearchResult = searchResult;
  }

  @action.bound
  public setSelectedCustomer(customer: CustomerData): void {
    this.selectedCustomer = customer;
  }

  @action.bound
  public setCustomerSearchParams(searchParams: CustomerSearchParams): void {
    this.customerSearchParams = searchParams;
  }

  @action.bound
  public pushCheckedCustomers(selectedCustomers: CustomerSearch): void {
    this.checkedCustomers.push(selectedCustomers);
  }

  @action.bound
  public removeCheckedCustomers(selectedCustomers: CustomerSearch): void {
    this.checkedCustomers = this.checkedCustomers.filter(
      (customer) => customer.customerGuid !== selectedCustomers.customerGuid,
    );
  }

  @action.bound
  public clearCheckedCustomers(): void {
    this.checkedCustomers = [];
  }
}
